import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { common } from '@mui/material/colors';
import { darken } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import {
  useAcceptSuggestion,
  useRejectSuggestion,
  useVetoApplication,
} from 'src/pages/AutoAcceptance/api';
import {
  useGetJobApplications,
  useGetJobApplicationsCount,
  useUpdateJobFavourite,
  useUpdateAcceptAllShifts,
  useUpdateRejectAllShifts,
  useUpdateAcceptShift,
  useUpdateRejectShift,
} from 'src/pages/ProjectDetailsPage/api';
import DataTable from 'src/components/DataTable';
import { forceDateTimeString, hoursToString } from 'src/utils/datetime';
import { colors } from 'src/utils/customColors';
import StudentLink from 'src/components/StudentLink';
import CheckButton from 'src/components/CheckButton';
import TextInput from 'src/components/TextInput';
import ConfirmModal from 'src/components/ConfirmModal';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { REJECT_REASON } from 'src/pages/ProjectDetailsPage/api/queries/useGetRejectReason';
import { ACCEPT_REASON } from 'src/pages/ProjectDetailsPage/api/queries/useGetAcceptReason';

import messages from '../../messages';
import { useStyles } from '../../JobDetails.styled';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import AcceptReasonModal from '../AcceptReasonModal/AcceptReasonModal';
import RejectReasonModal from '../RejectReasonModal/RejectReasonModal';
import StudentDetailsButton from '../StudentDetailsButton/StudentDetailsButton';
import { JobApplicationTabProps } from '../JobApplicationsAll/JobApplicationsAll';

import type { DefaultTheme } from '@mui/system';

const suggestionButtonSx = (color: string) => ({
  borderRadius: 1,
  minWidth: 100,
  backgroundColor: color,
  '&:hover': {
    backgroundColor: darken(color, 0.2),
  },
});

const textInputSx = (theme: DefaultTheme) => ({
  '& .MuiOutlinedInput-root': {
    height: theme.spacing(15),
    alignItems: 'flex-start',
  },
  '& .MuiInputBase-inputMultiline': {
    resize: 'none',
    overflowX: 'hidden',
    height: '100% !important',
    overflowY: 'auto !important',
  },
});

const sectionDividerSx = (theme: DefaultTheme) => ({
  padding: theme.spacing(1.5, 2),
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.grey[400],
});

const suggestionReasonChipSx = (suggested: boolean) => ({
  fontSize: 12,
  fontWeight: 500,
  backgroundColor: suggested ? colors.lightBlue : colors.lightRed,
});

export default function JobApplicationsApplied({
  job,
  jobUuid,
  filters,
  candidate,
  onCandidateSelect,
}: JobApplicationTabProps) {
  const classes = useStyles();
  const { formatMessage, formatNumber } = useIntl();

  const [params, setParams] = useState<Record<string, string | number>>({
    page: 1,
  });

  const { data: jobApplications, isLoading } = useGetJobApplications({
    jobUuid,
    type: 'applied',
    filters,
    params,
    enabled: Boolean(jobUuid),
  });

  const { suggestedCount } = useGetJobApplicationsCount({
    jobUuid,
    types: ['applied_count'],
    filters,
    enabled: Boolean(jobUuid),
  });

  const { mutate: acceptShift } = useUpdateAcceptShift();
  const { mutate: rejectShift } = useUpdateRejectShift();
  const { mutateAsync: acceptAllShifts, isPending: isPendingAccept } =
    useUpdateAcceptAllShifts();
  const { mutateAsync: rejectAllShifts, isPending: isPendingReject } =
    useUpdateRejectAllShifts();
  const { mutate: updateJobFavorite, isPending: isFavoritePending } =
    useUpdateJobFavourite();
  const { mutate: acceptSuggestion } = useAcceptSuggestion();
  const { mutate: rejectSuggestion } = useRejectSuggestion();
  const { mutate: vetoApplication } = useVetoApplication();

  const rejectModal = useReasonModal((id, reason) =>
    rejectSuggestion({ resultId: id, rejectionReason: reason }),
  );
  const vetoModal = useReasonModal((id, reason) =>
    vetoApplication({ applicationId: id, vetoReason: reason }),
  );

  const [selected, setSelected] = useState<UCM.JobApplicationType[]>([]);
  const [studentDetails, setStudentDetails] = useState(false);

  const badge = selected.length > 1 ? ` (${selected.length})` : '';

  const disabledButton =
    (!job?.isFlex && !selected.length) ||
    (job?.isFlex && (!selected.length || !filters.shift_date));

  const isSelectAll = useMemo(
    () =>
      jobApplications?.data && selected.length === jobApplications.data.length,
    [selected.length, jobApplications?.data],
  );

  useEffect(() => {
    setSelected([]);
  }, [filters.shift_date]);

  const handleFavorite = useCallback(
    (favorite: boolean, uuid: string) => {
      if (isFavoritePending) return;

      updateJobFavorite({ jobUuid, applicationUuid: uuid, favorite });
    },
    [jobUuid, isFavoritePending],
  );

  const handleAccept = useCallback(
    async (reason: string, bulk?: boolean) => {
      const promises = selected.map((item) =>
        bulk
          ? acceptAllShifts({ jobUuid, userUuid: item.applicant.uuid, reason })
          : acceptShift({ jobUuid, applicationUuid: item.uuid, reason }),
      );
      await Promise.all(promises).then(() => {
        setSelected([]);
      });
    },
    [selected],
  );

  const handleReject = useCallback(
    async (reason: string, bulk?: boolean) => {
      const promises = selected.map((item) =>
        bulk
          ? rejectAllShifts({ jobUuid, userUuid: item.applicant.uuid, reason })
          : rejectShift({
              jobUuid,
              reason,
              type: 'reject',
              applicationUuid: item.uuid,
            }),
      );
      await Promise.all(promises).then(() => {
        setSelected([]);
      });
    },
    [selected],
  );

  const checkIfExist = useCallback(
    (uuid: string) => {
      return selected.findIndex((item) => item.applicant.uuid === uuid) !== -1;
    },
    [selected],
  );

  const handleSelectAll = useCallback(() => {
    if (isSelectAll) {
      setSelected([]);
    } else if (jobApplications?.data) {
      const ids = jobApplications.data?.map((item) => item);
      setSelected(ids);
    }
  }, [jobApplications?.data, isSelectAll]);

  const handleSelect = useCallback(
    (item: UCM.JobApplicationType) => {
      const exist = checkIfExist(item.applicant.uuid);
      let local = [...selected];
      if (exist) {
        const filtered = local.filter(
          ({ applicant: { uuid } }) => item.applicant.uuid !== uuid,
        );
        local = filtered;
      } else {
        local.push(item);
      }
      setSelected(local);
    },
    [selected],
  );

  const pressCandidate = useCallback(
    (item: UCM.JobApplicationType | null) => {
      let candidate = (item as UCM.JobApplicationType | string) ?? null;

      if (studentDetails && item?.applicant) {
        candidate = item.applicant?.uuid;
      }

      onCandidateSelect?.(candidate);
    },
    [studentDetails, onCandidateSelect],
  );

  const handleAcceptSuggestion = useCallback((aaSuggestedId: number) => {
    acceptSuggestion(aaSuggestedId);
  }, []);

  const renderSectionRow = useCallback(
    ({ aaSuggestedId }: UCM.JobApplicationType, index: number) => {
      if (job?.isFlex && !filters.shift_date) return null;

      const currIsSuggested = aaSuggestedId != null;
      const prevRow = jobApplications?.data?.[index - 1];
      const prevIsSuggested = prevRow?.aaSuggestedId != null;

      if (prevRow && prevIsSuggested === currIsSuggested) return null;

      return (
        <Typography variant="subtitle2" fontWeight="bold" sx={sectionDividerSx}>
          {formatMessage(
            currIsSuggested
              ? messages.suggestedByAutoBooking
              : messages.notSuggested,
          )}
        </Typography>
      );
    },
    [jobApplications?.data, filters.shift_date, job?.isFlex],
  );

  const columns: Array<{
    id: keyof UCM.JobApplicationType | string;
    label: string | React.ReactNode;
  }> = useMemo(
    () => [
      {
        id: 'applicant',
        label: (
          <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
            <CheckButton
              onChange={handleSelectAll}
              active={Boolean(isSelectAll)}
            />
            <Typography
              variant="inherit"
              fontWeight={(theme) => theme.typography.fontWeightMedium}
            >
              {formatMessage(messages.applicantLabel)}
            </Typography>
            <StudentDetailsButton
              value={studentDetails}
              handleChange={setStudentDetails}
            />
          </Stack>
        ),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant) return '-';
          const { id, fullName, uuid } = item.applicant;
          const isSelected = checkIfExist(uuid);
          return (
            <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
              {(!filters.shift_date || !item.aaSuggestedId) && (
                <CheckButton
                  active={isSelected}
                  onChange={() => handleSelect(item)}
                />
              )}
              <FavoriteButton
                handleChange={(active) => handleFavorite(active, item.uuid)}
                active={item.favoriteManager}
              />
              <StudentLink label={`${id} - ${fullName}`} uuid={uuid} />
            </Stack>
          );
        },
      },
      {
        id: 'appliedCount',
        align: 'center',
        label: formatMessage(messages.appliedToLabel),
        render: (appliedCount: number | null) => (
          <Typography variant="inherit">{appliedCount ?? '-'}</Typography>
        ),
      },
      {
        id: 'yearlyWorkdaysCount',
        align: 'center',
        label: formatMessage(messages.workdayLabel),
        render: (yearlyWorkdaysCount: number | null) => (
          <Typography variant="inherit">
            {yearlyWorkdaysCount ?? '-'}
          </Typography>
        ),
      },
      {
        id: 'cancelRate',
        align: 'center',
        label: formatMessage(messages.cancelRateLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.applicationsCancelledRatio) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.applicationsCancelledRatio}%
            </Typography>
          );
        },
      },
      {
        id: 'timeToConfirm',
        align: 'center',
        label: formatMessage(messages.timeToConfirmLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.confirmationTime) return '-';
          return (
            <Typography variant="inherit">
              {hoursToString(item.applicant.confirmationTime)}
            </Typography>
          );
        },
      },
      {
        id: 'initialScore',
        align: 'center',
        label: formatMessage(messages.initialScoreColumnLabel),
        size: 'small',
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.initialRatingScore) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.initialRatingScore}
            </Typography>
          );
        },
      },
      {
        id: 'ratingScore',
        align: 'center',
        label: formatMessage(messages.ratingLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.ratingScore) return '-';
          return (
            <Typography variant="inherit">
              {item.applicant.ratingScore}
            </Typography>
          );
        },
      },
      {
        id: 'prescreeningRating',
        align: 'center',
        label: formatMessage(messages.preScreenLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.prescreeningRating) return '-';
          const { prescreeningRating } = item.applicant;

          return (
            <Tooltip
              arrow
              title={
                <Stack spacing={1}>
                  {Object.entries(prescreeningRating).map(([key, value]) => (
                    <Typography
                      key={key}
                      variant="inherit"
                      textTransform="capitalize"
                    >
                      {key}: {value}
                    </Typography>
                  ))}
                </Stack>
              }
            >
              <Typography variant="inherit">
                {prescreeningRating.average}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        id: 'profile',
        align: 'center',
        label: formatMessage(messages.profileLabel),
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.applicant.status) return '-';
          let label = formatMessage(messages.completeLabel);
          if (item.applicant.status.code === 'required')
            label = formatMessage(messages.incompleteLabel);
          return <Typography variant="inherit">{label}</Typography>;
        },
      },
      {
        id: 'lastWorkInDays',
        align: 'center',
        label: formatMessage(messages.lastJobLabel),
        render: (lastWorkInDays: number | null) => {
          if (!lastWorkInDays) return '-';

          let tooltipMessage;
          const days = Math.abs(lastWorkInDays);

          if (lastWorkInDays === 0) {
            tooltipMessage = formatMessage(messages.applicantWorksToday);
          } else if (lastWorkInDays > 0) {
            tooltipMessage = formatMessage(
              days === 1
                ? messages.applicantWorkedYesterday
                : messages.applicantWorkedDaysAgo,
              { days },
            );
          } else {
            tooltipMessage = formatMessage(
              days === 1
                ? messages.applicantWillWorkTomorrow
                : messages.applicantWillWorkInDays,
              { days },
            );
          }

          return (
            <Tooltip arrow title={tooltipMessage}>
              <Typography variant="inherit">{`${lastWorkInDays}d`}</Typography>
            </Tooltip>
          );
        },
      },
      {
        id: 'daysSinceApplied',
        align: 'center',
        label: formatMessage(messages.daysSinceAppliedLabel),
        render: (daysSinceApplied: string) => (
          <Typography variant="inherit">{daysSinceApplied}</Typography>
        ),
      },
    ],
    [
      studentDetails,
      checkIfExist,
      handleSelect,
      handleFavorite,
      handleSelectAll,
    ],
  );

  const autoAcceptanceColumns = useMemo(
    () => [
      {
        id: 'abReasoning',
        label: formatMessage(messages.abReasoningLabel),
        align: 'center',
        render: (_: Object, item: UCM.JobApplicationType) => {
          if (!item.abReasoning?.length) return '-';
          return (
            <Stack
              gap={0.5}
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="center"
            >
              {item.abReasoning.map(({ code, text: reason }) => {
                if (reason == null) return null;
                return (
                  <Chip
                    key={code}
                    size="small"
                    label={reason}
                    sx={suggestionReasonChipSx(Boolean(item.aaSuggestedId))}
                  />
                );
              })}
            </Stack>
          );
        },
      },
      {
        id: 'aaSuggestedId',
        label: formatMessage(messages.suggestionLabel),
        align: 'center',
        render: (_: string | null, item: UCM.JobApplicationType) => {
          const { aaSuggestedId, aaSkippedId } = item;

          if (aaSuggestedId != null && aaSkippedId == null) {
            return (
              <Grid gap={1} container justifyContent="center">
                <ConfirmationDialog
                  onConfirm={() => handleAcceptSuggestion(aaSuggestedId)}
                  message={formatMessage(messages.acceptSuggestionConfirm)}
                >
                  {(handleDialogOpen) => (
                    <Button
                      variant="contained"
                      onClick={handleDialogOpen}
                      sx={suggestionButtonSx(colors.greenSecondary)}
                    >
                      {formatMessage(messages.acceptSuggestionButton)}
                    </Button>
                  )}
                </ConfirmationDialog>
                <Button
                  variant="contained"
                  sx={suggestionButtonSx(colors.darkRed)}
                  onClick={(event) => rejectModal.open(event, aaSuggestedId)}
                >
                  {formatMessage(messages.rejectSuggestionButton)}
                </Button>
              </Grid>
            );
          }

          if (aaSkippedId != null && aaSuggestedId == null) {
            return (
              <Button
                variant="outlined"
                onClick={(event) => vetoModal.open(event, aaSkippedId)}
              >
                {formatMessage(messages.vetoButton)}
              </Button>
            );
          }

          return '-';
        },
      },
    ],
    [],
  );

  const validityColumns = useMemo(
    () => [
      {
        id: 'validityScore',
        label: formatMessage(messages.validityScoreLabel),
        render: (score: number | null | undefined) => {
          if (score == null) return '-';

          return formatNumber(score, {
            style: 'percent',
            maximumFractionDigits: 0,
          });
        },
      },
      {
        id: 'lastRenewalAt',
        label: formatMessage(messages.lastRenewalLabel),
        render: (lastRenewalAt: string | null | undefined) => {
          if (!lastRenewalAt) return '-';

          return forceDateTimeString(lastRenewalAt);
        },
      },
    ],
    [formatMessage, formatNumber],
  );

  const allColumns = useMemo(
    () => [
      ...columns,
      ...validityColumns,
      ...(!job?.isFlex || filters.shift_date ? autoAcceptanceColumns : []),
    ],
    [
      columns,
      validityColumns,
      autoAcceptanceColumns,
      filters.shift_date,
      job?.isFlex,
    ],
  );

  const handlePagination = useCallback((_: unknown, page: number) => {
    setParams({ page: page + 1 });
  }, []);

  const tableFooterSx = useCallback(
    (theme: DefaultTheme) => ({
      borderTop: `thin solid ${theme.palette.grey[200]}`,
    }),
    [],
  );

  return (
    <Grid size={{ xs: 12 }}>
      <DataTable
        loading={isLoading}
        columns={allColumns}
        activeRow={candidate}
        pressRow={pressCandidate}
        checkActiveKey="applicant.id"
        rows={jobApplications?.data}
        onPaginate={handlePagination}
        pagination={jobApplications?.meta}
        className={classes.tableContainer}
        renderSectionRow={renderSectionRow}
      />
      <Stack
        p={2}
        gap={3}
        flexDirection="row"
        alignItems="center"
        sx={tableFooterSx}
        bgcolor={common.white}
        justifyContent={
          filters.shift_date || !job?.isFlex ? 'space-between' : 'flex-end'
        }
      >
        {filters.shift_date || !job?.isFlex ? (
          <Typography variant="body2" color="text.secondary">
            {formatMessage(messages.suggestedCount, {
              count: suggestedCount,
            })}
          </Typography>
        ) : null}

        <Stack flexDirection="row" gap={1}>
          <AcceptReasonModal
            onConfirm={handleAccept}
            isLoading={isPendingAccept}
            enabledFetch={Boolean(jobUuid)}
            disabledButton={disabledButton}
            type={ACCEPT_REASON.ACCEPTANCE_APPLICATION}
            modalLabel={formatMessage(messages.acceptModalLabel)}
            disabledBulkButton={
              !job?.isFlex || !selected.length || selected.length > 1
            }
            buttonLabel={`${formatMessage(messages.confirmAcceptButton)}${badge}`}
            bulkButtonLabel={`${formatMessage(messages.acceptBulkButton)}${badge}`}
          />

          <RejectReasonModal
            onConfirm={handleReject}
            isLoading={isPendingReject}
            enabledFetch={Boolean(jobUuid)}
            disabledButton={disabledButton}
            type={REJECT_REASON.REJECT_APPLICATION}
            modalLabel={formatMessage(messages.rejectModalLabel)}
            disabledBulkButton={!job?.isFlex || !selected.length}
            buttonLabel={`${formatMessage(messages.confirmRejectButton)}${badge}`}
            bulkButtonLabel={`${formatMessage(messages.rejectBulkButton)}${badge}`}
          />
        </Stack>
      </Stack>

      <ConfirmModal
        width={500}
        loading={false}
        open={rejectModal.isOpen}
        onClose={rejectModal.close}
        onConfirm={rejectModal.confirm}
        renderExtraButton={
          <Button variant="outlined" onClick={rejectModal.close}>
            {formatMessage(messages.cancelRejectButton)}
          </Button>
        }
        disabled={!rejectModal.reason.trim()}
        title={formatMessage(messages.rejectionReasonTitle)}
        submitTextButton={formatMessage(messages.confirmButton)}
      >
        <Typography color="text.secondary">
          {formatMessage(messages.rejectionReasonHelper)}
        </Typography>
        <TextInput
          fullWidth
          multiline
          sx={textInputSx}
          variant="outlined"
          value={rejectModal.reason}
          label={formatMessage(messages.reasonLabel)}
          onChange={(event) => rejectModal.setReason(event.target.value)}
        />
      </ConfirmModal>

      <ConfirmModal
        width={500}
        loading={false}
        open={vetoModal.isOpen}
        onClose={vetoModal.close}
        onConfirm={vetoModal.confirm}
        renderExtraButton={
          <Button variant="outlined" onClick={vetoModal.close}>
            {formatMessage(messages.cancelRejectButton)}
          </Button>
        }
        disabled={!vetoModal.reason.trim()}
        title={formatMessage(messages.vetoReasonTitle)}
        submitTextButton={formatMessage(messages.confirmButton)}
      >
        <Typography color="text.secondary">
          {formatMessage(messages.vetoReasonHelper)}
        </Typography>
        <TextInput
          fullWidth
          multiline
          sx={textInputSx}
          variant="outlined"
          value={vetoModal.reason}
          label={formatMessage(messages.reasonLabel)}
          onChange={(event) => vetoModal.setReason(event.target.value)}
        />
      </ConfirmModal>
    </Grid>
  );
}

const useReasonModal = (onSubmit: (id: number, reason: string) => void) => {
  const [targetId, setTargetId] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const open = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, id: number | null) => {
      e.stopPropagation();
      setTargetId(id);
      setReason('');
    },
    [],
  );

  const close = useCallback(() => {
    setTargetId(null);
    setReason('');
  }, []);

  const confirm = useCallback(() => {
    const trimmed = reason.trim();
    if (!targetId || !trimmed) return;
    onSubmit(targetId, trimmed);
    close();
  }, [targetId, reason]);

  return { isOpen: !!targetId, reason, setReason, open, close, confirm };
};
